/* deadwax theme — global JS.
   Module seams filled by later tasks:
     deadwax.cart      (Task 8)  add/change/render/open/close
     deadwax.player    (Task 15) playProduct
     deadwax.wishlist  (Task 17) toggle/render
     deadwax.hunt      (Task 18) open
*/
// theme.liquid pre-seeds window.deadwax with `config` before this file loads, so a
// plain `|| {…}` would keep that partial object and leave money/cart/etc. undefined.
// Ensure every namespace exists whether or not deadwax was already defined.
window.deadwax = window.deadwax || {};
deadwax.cart = deadwax.cart || {};
deadwax.player = deadwax.player || {};
deadwax.wishlist = deadwax.wishlist || {};
deadwax.money = deadwax.money || {};

// ---- colour theme: follows the device until the visitor makes an explicit choice ----
deadwax.theme = (function () {
  const KEY = 'dw:theme';
  const media = window.matchMedia('(prefers-color-scheme: light)');
  let inited = false;

  function stored() {
    try {
      const value = localStorage.getItem(KEY);
      return value === 'light' || value === 'dark' ? value : null;
    } catch (_) { return null; }
  }
  function current() {
    return document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';
  }
  function refreshControls() {
    const isLight = current() === 'light';
    const nextLabel = isLight ? 'Switch to dark mode' : 'Switch to light mode';
    document.querySelectorAll('[data-theme-toggle]').forEach(button => {
      button.setAttribute('aria-label', nextLabel);
      button.setAttribute('title', nextLabel);
    });
    document.querySelectorAll('[data-theme-label]').forEach(label => {
      label.textContent = isLight ? 'Dark mode' : 'Light mode';
    });
  }
  function apply(theme) {
    const value = theme === 'light' ? 'light' : 'dark';
    document.documentElement.dataset.theme = value;
    document.documentElement.style.colorScheme = value;
    const meta = document.getElementById('themeColor');
    if (meta) meta.content = value === 'light' ? '#F7F7F8' : '#0B0B0D';
    refreshControls();
  }
  function toggle() {
    const next = current() === 'light' ? 'dark' : 'light';
    try { localStorage.setItem(KEY, next); } catch (_) {}
    apply(next);
  }
  function init() {
    if (inited) return;
    inited = true;
    document.addEventListener('click', event => {
      if (event.target.closest('[data-theme-toggle]')) toggle();
    });
    media.addEventListener('change', event => {
      if (!stored()) apply(event.matches ? 'light' : 'dark');
    });
  }
  function refresh() {
    apply(stored() || (media.matches ? 'light' : 'dark'));
  }
  return { init, refresh, toggle };
})();

// ---- pure helpers (ported from prototype) ----
deadwax.slug = function (s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
};

// Markets-aware money. Server-rendered prices use the Liquid `money` filter;
// this is for JS-built markup (cart drawer line items, etc.).
deadwax.money.format = function (cents) {
  const fmt = (window.Shopify && Shopify.money_format) || '${{amount}}';
  if (window.Shopify && typeof Shopify.formatMoney === 'function') {
    return Shopify.formatMoney(cents, fmt);
  }
  // minimal fallback if Shopify's currency.js isn't present
  return fmt.replace(/\{\{\s*amount\s*\}\}/, (cents / 100).toFixed(2));
};

// ---- cart (Shopify Ajax) + drawer ----
deadwax.cart = (function () {
  // 0 = no free-shipping threshold for this country (rest of world) — keep it 0, don't fall back.
  const THRESH = (deadwax.config && deadwax.config.freeShip != null) ? deadwax.config.freeShip : 15000; // cents
  const UPSELL_FROM = (deadwax.config && deadwax.config.upsellFrom) || 10000; // only nudge once the cart is this close
  const $ = id => document.getElementById(id);
  const money = c => deadwax.money.format(c);

  const getCart = () => fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json());

  // Per-variant available stock, sourced from Liquid via data-stock on add buttons
  // (/cart.js carries no inventory). In-memory map survives Turbo navigations; a
  // single-stock line hides its qty stepper. Unknown stock falls back to the stepper.
  const stock = {};
  function scanStock() {
    document.querySelectorAll('[data-add][data-stock]').forEach(b => {
      const id = +b.dataset.add, n = +b.dataset.stock;
      if (id && n > 0) stock[id] = n;
    });
  }

  const applyCount = c => {
    // Update every cart-count element (header pill + bottom tab-bar badge). The floating tab
    // badge (.mtab-badge) hides at 0; the header's inline "Cart N" text always shows.
    document.querySelectorAll('.js-cart-count').forEach(el => {
      el.textContent = c.item_count;
      if (el.classList.contains('mtab-badge')) el.style.display = c.item_count > 0 ? 'flex' : 'none';
    });
  };
  async function syncCount() { const c = await getCart(); applyCount(c); return c; }

  async function add(variantId, qty, extra, btn) {
    // Instant feedback: spin the button and open the drawer in a loading state
    // *before* any request, so the click never feels dead while the network runs.
    if (btn) btn.classList.add('is-adding');
    if (btn && btn.dataset.stock) stock[variantId] = +btn.dataset.stock;
    showDrawer(); loadingState();
    try {
      const properties = {};
      if (extra && extra.artist) properties._artist = extra.artist;
      // Stamp available stock onto the line (hidden _ property) so the drawer knows it
      // on any page, not just where the product card is rendered. Falls back to the map.
      const max = (extra && extra.max) || stock[variantId];
      if (max) properties._max = String(max);
      const res = await fetch('/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: qty || 1, properties }),
      });
      if (!res.ok) { render(); return; }
      // Funnel: record the add (handle comes back on the /cart/add.js line item).
      try { const it = await res.clone().json(); if (deadwax.journey) deadwax.journey.send('atc', it && it.handle); } catch (e) {}
      const c = await getCart();        // single cart read; feeds both count and drawer
      applyCount(c); render(c);
    } catch (e) { render(); }
    finally { if (btn) btn.classList.remove('is-adding'); }
  }
  async function change(key, quantity) {
    await fetch('/cart/change.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    const c = await getCart(); applyCount(c); render(c);
  }
  function shipProgress(sub) {
    if (THRESH <= 0) return ''; // no free-shipping threshold here (rest of world) — no bar
    if (sub >= THRESH) return `<div class="freeship hit"><div class="fs-t">Free shipping unlocked. Let’s go.</div><div class="fs-s">The whole lot ships on us.</div></div>`;
    const pct = Math.min(100, sub / THRESH * 100), away = THRESH - sub;
    return `<div class="freeship"><div class="fs-row"><span>${money(away)} to go for free shipping</span><span>${Math.round(pct)}%</span></div><div class="fs-bar"><i style="--progress:${pct / 100}"></i></div></div>`;
  }
  const socialFollow = () => (deadwax.config && deadwax.config.socialFollow) ? `<div class="dempty-soc">${deadwax.config.socialFollow}</div>` : '';
  const empty = (a, b) => `<div class="dempty"><svg viewBox="0 0 100 100"><use href="#skull"/></svg><div>${a}<br>${b}</div>${socialFollow()}</div>`;
  function row(item) {
    const artist = (item.properties && item.properties._artist) || item.vendor || '';
    const img = item.image ? `<img src="${item.image}" alt="">` : '';
    // Prefer the stock stamped on the line at add time (works on any page); fall back
    // to the on-page card map for lines added before this existed.
    const max = (item.properties && item.properties._max != null) ? +item.properties._max : stock[item.variant_id];
    // Single-stock: no stepper (you can only ever hold one). Known multi-stock: cap the +.
    const atMax = max != null && item.quantity >= max;
    const q = max === 1 ? '' : `<div class="miniqty"><button data-key="${item.key}" data-delta="-1" ${item.quantity <= 1 ? 'disabled' : ''}>−</button><span>${item.quantity}</span><button data-key="${item.key}" data-delta="1" ${atMax ? 'disabled' : ''}>+</button></div>`;
    return `<div class="ditem" data-key="${item.key}"><div class="th">${img}</div>
      <div class="di"><div class="t">${item.product_title}</div><div class="a">${artist}</div></div>
      <div class="right"><div class="pr">${money(item.final_line_price)}</div>${q}<span class="rm" data-remove="${item.key}" title="Remove">✕</span></div></div>`;
  }
  function loadingState() {
    const head = $('drawerHead'), body = $('drawerBody');
    if (head) head.innerHTML = `<h3>Your cart</h3><span class="x" data-close>✕</span>`;
    if (body) body.innerHTML = `<div class="dloading"><span class="dspin"></span></div>`;
  }
  async function render(cart) {
    const head = $('drawerHead'), body = $('drawerBody');
    if (!head || !body) return;
    scanStock();
    const c = cart || await getCart();
    head.innerHTML = `<h3>Your cart<small>${c.item_count} items</small></h3><span class="x" data-close>✕</span>`;
    if (!c.item_count) { body.innerHTML = empty('Your cart is empty.', 'Add some wax to get started.'); return; }
    const upsell = deadwax.suggest ? deadwax.suggest.build(c, THRESH, money, UPSELL_FROM) : '';
    body.innerHTML = `<div class="ditems">${c.items.map(row).join('')}</div>
      <div class="dfoot">${shipProgress(c.total_price)}${upsell}<div class="sub"><span>Subtotal</span><b>${money(c.total_price)}</b></div>
      <a class="btn btn-primary" href="/checkout" data-turbo="false">Checkout</a></div>`;
  }
  function showDrawer() { $('drawer').dataset.mode = 'cart'; $('drawer').classList.add('up'); $('scrim').classList.add('up'); }
  function open() { showDrawer(); render(); }
  function close() { const d = $('drawer'), s = $('scrim'); if (d) d.classList.remove('up'); if (s) s.classList.remove('up'); }

  function init() {
    syncCount();
    document.addEventListener('click', function (e) {
      const addBtn = e.target.closest('[data-add]');
      if (addBtn) { e.preventDefault(); e.stopPropagation(); add(+addBtn.dataset.add, 1, { artist: addBtn.dataset.artist }, addBtn); return; }
      if (e.target.closest('[data-close]') || e.target.id === 'scrim') { close(); return; }
      const step = e.target.closest('[data-key][data-delta]');
      if (step) { const span = step.parentElement.querySelector('span'); change(step.dataset.key, Math.max(0, (+span.textContent) + (+step.dataset.delta))); return; }
      const rm = e.target.closest('[data-remove]');
      if (rm) { change(rm.dataset.remove, 0); return; }
    });
  }
  return { add, change, render, open, close, init };
})();

// ---- Cart upsell: "one more for free shipping". When the cart is close to the threshold, suggest
// ONE record by priority: a saved (wishlist) record, else the one they've viewed most and not bought,
// else the cart's dominant genre. ----
deadwax.suggest = (function () {
  let pool = null;
  function getPool() {
    if (pool) return pool;
    const el = document.getElementById('dw-suggest');
    try { pool = el ? JSON.parse(el.textContent) : []; } catch (e) { pool = []; }
    return pool;
  }
  const esc = s => String(s || '').replace(/[&<>"]/g, m => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[m]));

  // Pick ONE candidate {v,h,t,a,i,priceStr,why} by priority, or null.
  function choose(cart, money) {
    const inCart = new Set(cart.items.map(i => i.handle));
    const p = getPool();
    const byHandle = {}; p.forEach(x => { byHandle[x.h] = x; });

    // 1) wishlist — the priciest saved record not already bought/carted (rank by the pool's cents, not
    //    the display string, so it's correct in any currency format)
    const wl = (deadwax.wishlist && deadwax.wishlist.list) ? deadwax.wishlist.list() : [];
    const wlCands = wl.filter(w => w.variant && w.handle && !w.sold && !inCart.has(w.handle))
      .map(w => ({ v: w.variant, h: w.handle, t: w.title, a: w.artist, i: w.art, priceStr: w.price, pc: byHandle[w.handle] ? byHandle[w.handle].p : 0, why: 'From your wishlist' }))
      .sort((a, b) => b.pc - a.pc);
    if (wlCands.length) return wlCands[0];

    // 2) most-viewed record not in the cart (onsite journey)
    const views = (deadwax.journey && deadwax.journey.views) ? deadwax.journey.views() : {};
    const viewed = Object.keys(views)
      .filter(h => !inCart.has(h) && views[h].d && views[h].d.v)
      .sort((a, b) => (views[b].n || 0) - (views[a].n || 0));
    if (viewed.length) {
      const d = views[viewed[0]].d;
      return { v: d.v, h: d.h, t: d.t, a: d.a, i: d.i, priceStr: money(d.p), why: views[viewed[0]].n > 1 ? 'You keep coming back to this' : 'You were just looking at this' };
    }

    // 3) genre fallback — priciest addable record in the cart's dominant genre
    const counts = {};
    cart.items.forEach(i => { const x = byHandle[i.handle]; if (x && x.g) counts[x.g] = (counts[x.g] || 0) + i.quantity; });
    let genre = null, best = 0;
    Object.keys(counts).forEach(g => { if (counts[g] > best) { best = counts[g]; genre = g; } });
    let cands = p.filter(x => x.v && x.h && !inCart.has(x.h));
    if (genre) { const g = cands.filter(x => x.g === genre); if (g.length) cands = g; }
    if (!cands.length) return null;
    cands.sort((a, b) => b.p - a.p);
    const x = cands[0];
    return { v: x.v, h: x.h, t: x.t, a: x.a, i: x.i, priceStr: money(x.p), why: genre ? 'More ' + genre : 'You might like this' };
  }

  // Returns upsell HTML, or '' when the cart is empty / already qualifies / too early / nothing to suggest.
  function build(cart, thresh, money, from) {
    if (!cart || !cart.item_count) return '';
    const remaining = thresh - cart.total_price;
    if (remaining <= 0) return '';                   // already unlocked free shipping
    if (from && cart.total_price < from) return '';  // only nudge once they're close
    const c = choose(cart, money);
    if (!c) return '';
    return `<div class="upsell"><div class="us-head">Almost there — <b>${money(remaining)}</b> to free shipping</div>` +
      `<button type="button" class="us-card" data-add="${c.v}" data-artist="${esc(c.a)}" data-title="${esc(c.t)}" aria-label="Add ${esc(c.t)} to cart">` +
        `<span class="us-art">${c.i ? `<img src="${esc(c.i)}" alt="" loading="lazy">` : ''}</span>` +
        `<span class="us-info"><span class="us-why">${esc(c.why)}</span><span class="us-t">${esc(c.t)}</span><span class="us-a">${esc(c.a)}</span></span>` +
        `<span class="us-pr">${esc(c.priceStr)}</span><span class="us-add" aria-hidden="true">+</span>` +
      `</button></div>`;
  }
  return { build };
})();

// ---- Onsite journey: record product views so the upsell can suggest the record you keep coming
// back to. Anonymous → localStorage. Logged-in → also persisted server-side via the Shopify App
// Proxy (/apps/deadwax/journey → engine), so it follows the customer across devices and feeds CRM. ----
deadwax.journey = (function () {
  const KEY = 'dw:views', CAP = 40, PROXY = '/apps/deadwax/journey';
  const loggedIn = !!(deadwax.config && deadwax.config.loggedIn);
  let server = null; // {handle: {n, d}} once fetched for a logged-in customer
  // ---- Anonymous funnel events (view / atc / checkout): every visitor gets a random id in
  // localStorage (no PII) and events flow through the same App Proxy into D1, so the admin
  // Interest page can show all-traffic drop-offs, not just the logged-in slice. ----
  function aid() {
    try {
      let a = localStorage.getItem('dw:aid');
      if (!a) {
        a = (window.crypto && crypto.randomUUID) ? crypto.randomUUID().replace(/-/g, '')
          : Date.now().toString(36) + Math.random().toString(36).slice(2, 12);
        localStorage.setItem('dw:aid', a);
      }
      return a;
    } catch (e) { return null; } // storage blocked → skip silently
  }
  function send(ev, h) {
    const a = aid(); if (!a) return;
    fetch(PROXY + '/event', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ e: ev, h: h || null, aid: a }), keepalive: true,
    }).catch(() => {});
  }
  // Checkout starts: the drawer's Checkout link and any standard cart form checkout button.
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('a[href^="/checkout"], [name="checkout"]')) send('checkout');
  });
  function load() { try { return JSON.parse(localStorage.getItem(KEY) || '{}'); } catch (e) { return {}; } }
  function save(v) { try { localStorage.setItem(KEY, JSON.stringify(v)); } catch (e) {} }
  function track() {
    const el = document.getElementById('dw-current-product');
    if (!el) return;
    // The first page load fires BOTH DOMContentLoaded and turbo:load, each calling track().
    // Stamp the rendered element so one page render counts once — Turbo swaps the body per
    // navigation, so each new visit arrives unstamped.
    if (el.dataset.dwTracked) return;
    el.dataset.dwTracked = '1';
    let d; try { d = JSON.parse(el.textContent); } catch (e) { return; }
    if (!d || !d.h) return;
    const v = load();
    const cur = v[d.h] || { n: 0 };
    cur.n += 1; cur.t = Date.now(); cur.d = d;
    v[d.h] = cur;
    const keys = Object.keys(v);
    if (keys.length > CAP) keys.sort((a, b) => (v[b].t || 0) - (v[a].t || 0)).slice(CAP).forEach(k => delete v[k]);
    save(v);
    if (server) { server[d.h] = { n: (server[d.h] ? server[d.h].n : 0) + 1, d: d }; }
    send('view', d.h); // all traffic — feeds the admin funnel
    // Fire-and-forget; .catch so a blocked/absent proxy never throws an unhandled rejection.
    if (loggedIn) { fetch(PROXY, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(d), keepalive: true }).catch(() => {}); }
  }
  // Pull the customer's server-side views once so the upsell (sync) can use them.
  function syncServer() {
    if (!loggedIn) return;
    fetch(PROXY, { headers: { Accept: 'application/json' } })
      .then(r => r.ok ? r.json() : null)
      .then(j => { if (j && j.views) { const m = {}; j.views.forEach(x => { m[x.h] = { n: x.n, d: x }; }); server = m; } })
      .catch(() => {});
  }
  // Views for the upsell: server (cross-device) merged with local, taking the higher count per handle
  // so the current session's just-recorded view isn't masked by a stale server snapshot.
  function views() {
    const local = load();
    if (!server) return local;
    const merged = {};
    new Set([...Object.keys(local), ...Object.keys(server)]).forEach(h => {
      const l = local[h], s = server[h];
      if (l && s) merged[h] = (l.n || 0) >= (s.n || 0) ? l : s; // ties → local (carries the fresh view data)
      else merged[h] = l || s;
    });
    return merged;
  }
  return { track, views, syncServer, send };
})();

// ---- Preview player: our own bottom bar playing per-track R2 audio clips. Member-only —
// the bar and the track urls (in track-data) only render for logged-in customers. The queue
// walks the record's tracklist (prev/next). Audio survives Turbo nav (data-turbo-permanent). ----
deadwax.player = (function () {
  const audio = new Audio(); audio.preload = 'none';
  const $ = id => document.getElementById(id);
  const fmt = s => { s = Math.max(0, Math.floor(s || 0)); return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0'); };
  let queue = [], qi = 0, playing = false, cur = null, inited = false; // cur = {handle, d}

  function dataFor(handle) {
    const el = document.querySelector('.dw-play[data-handle="' + (window.CSS && CSS.escape ? CSS.escape(handle) : handle) + '"]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }
  function playProduct(handle, idx) {
    const d = dataFor(handle);
    if (!d || !d.tracks || !d.tracks.length) return;
    // Only tracks with a (member) audio url are playable.
    if (!d.tracks.some(t => t.url)) return;
    cur = { handle, d }; queue = d.tracks.map((_, k) => k);
    qi = Math.max(0, Math.min(d.tracks.length - 1, idx || 0));
    startCurrent();
  }
  function startCurrent() {
    const d = cur.d, t = d.tracks[qi];
    if (!t.url) { if (qi < queue.length - 1) { qi++; return startCurrent(); } return; }
    $('player').classList.add('loading');
    audio.src = t.url; audio.play().catch(() => {}); playing = true;
    $('ptit').textContent = t.name;
    $('part').textContent = d.artist + (d.catno ? ' · ' + d.catno : '');
    $('pth').innerHTML = d.art ? '<img src="' + d.art + '" alt="">' : '';
    $('qn').textContent = queue.length > 1 ? (qi + 1) + '/' + queue.length : '';
    $('pNext').disabled = qi >= queue.length - 1;
    if (deadwax.wishlist.has) $('pHeart').classList.toggle('on', deadwax.wishlist.has(d.id));
    $('player').classList.add('up'); reflect(); highlight();
  }
  function nextTrack() { if (qi < queue.length - 1) { qi++; startCurrent(); } }
  function prevTrack() { if (audio.currentTime > 3 || qi === 0) { audio.currentTime = 0; return; } qi--; startCurrent(); }
  function toggle() { if (!queue.length) return; if (playing) { audio.pause(); playing = false; } else { audio.play(); playing = true; } reflect(); }
  function stop() {
    audio.pause(); playing = false; queue = []; cur = null;
    const p = $('player');
    if (p) { p.classList.remove('up'); p.classList.remove('loading'); }
    highlight(); reflect();
  }
  function setPP() { const i = $('ppIcon'); if (i) i.innerHTML = playing ? '<path d="M6 5h4v14H6zM14 5h4v14h-4z"/>' : '<path d="M8 5v14l11-7z"/>'; }
  function reflect() {
    setPP();
    document.querySelectorAll('.is-playing').forEach(el => el.classList.remove('is-playing'));
    if (playing && cur) {
      document.querySelectorAll('[data-rec="' + cur.d.id + '"]').forEach(el => el.classList.add('is-playing'));
      const trk = document.querySelector('.trk[data-t="' + cur.handle + '-' + qi + '"]'); if (trk) trk.classList.add('is-playing');
    }
  }
  function highlight() {
    document.querySelectorAll('.playing').forEach(el => el.classList.remove('playing'));
    if (!cur) return;
    document.querySelectorAll('[data-rec="' + cur.d.id + '"]').forEach(el => el.classList.add('playing'));
    const trk = document.querySelector('.trk[data-t="' + cur.handle + '-' + qi + '"]'); if (trk) trk.classList.add('playing');
  }
  audio.addEventListener('timeupdate', () => {
    if (!audio.duration) return;
    $('progI').style.width = (audio.currentTime / audio.duration * 100) + '%';
    $('ptime').textContent = fmt(audio.currentTime); $('pdur').textContent = fmt(audio.duration);
  });
  audio.addEventListener('ended', () => { if (qi < queue.length - 1) { qi++; startCurrent(); } else { stop(); } });
  const clearLoad = () => { const p = $('player'); if (p) p.classList.remove('loading'); };
  audio.addEventListener('playing', clearLoad);
  audio.addEventListener('canplay', clearLoad);
  audio.addEventListener('waiting', () => { if (playing) $('player').classList.add('loading'); });
  audio.addEventListener('error', clearLoad);
  audio.addEventListener('play', () => { playing = true; reflect(); });
  audio.addEventListener('pause', () => { playing = false; reflect(); });

  function init() {
    if (inited) return; // boot() runs on DOMContentLoaded + every turbo:load — wire listeners once
    inited = true;
    const pp = $('pp'); if (pp) pp.addEventListener('click', toggle);
    const pv = $('pPrev'); if (pv) pv.addEventListener('click', prevTrack);
    const nx = $('pNext'); if (nx) nx.addEventListener('click', nextTrack);
    const cl = $('pClose'); if (cl) cl.addEventListener('click', stop);
    const pr = $('prog'); if (pr) pr.addEventListener('click', e => { if (!audio.duration) return; const r = e.currentTarget.getBoundingClientRect(); audio.currentTime = (e.clientX - r.left) / r.width * audio.duration; });
    const pc = $('pCart'); if (pc) pc.addEventListener('click', () => { if (cur) deadwax.cart.add(cur.d.variantId, 1, { artist: cur.d.artist }, pc); });
    const ph = $('pHeart'); if (ph) ph.addEventListener('click', () => { if (cur && deadwax.wishlist.toggle) deadwax.wishlist.toggle(cur.d.id, ph); });
    const pt = $('pth'); if (pt) { pt.title = 'View record'; pt.addEventListener('click', () => { if (cur) window.location.href = '/products/' + cur.handle; }); }
    document.addEventListener('click', e => {
      const el = e.target.closest('[data-play]');
      if (el) {
        e.preventDefault(); e.stopPropagation();
        const h = el.dataset.play, idx = el.dataset.track ? +el.dataset.track : 0;
        if (cur && cur.handle === h && qi === idx) toggle(); else playProduct(h, idx);
      }
    });
    // The Booth owns its own audio element. Stop this persistent player before
    // Turbo swaps the page so the two playback systems can never overlap.
    document.addEventListener('turbo:before-visit', e => {
      try {
        const path = new URL(e.detail.url, location.href).pathname.replace(/\/+$/, '');
        if (path === '/pages/flip') stop();
      } catch (_) {}
    });
  }
  return { playProduct, nextTrack, prevTrack, toggle, stop, init, refresh: highlight };
})();

// Carousel arrows: scroll the nearest .grid.onerow in the section by ~a viewport width.
document.addEventListener('click', function (e) {
  const a = e.target.closest && e.target.closest('[data-scroll]');
  if (!a) return;
  const sec = a.closest('section'), row = sec && sec.querySelector('.grid.onerow');
  if (!row) return;
  const dir = a.getAttribute('data-scroll') === 'next' ? 1 : -1;
  row.scrollBy({ left: Math.round(row.clientWidth * 0.8) * dir, behavior: 'smooth' });
});

// Currency picker: the .curbtn toggles .curmenu.up inline; close it when clicking outside
// the picker or pressing Escape. Delegated on document, wired once (theme.js runs once).
function closeCurMenus() {
  document.querySelectorAll('.curmenu.up').forEach(function (m) {
    m.classList.remove('up');
    const btn = m.parentElement && m.parentElement.querySelector('.curbtn');
    if (btn) btn.setAttribute('aria-expanded', 'false');
  });
}
document.addEventListener('click', function (e) {
  if (e.target.closest && e.target.closest('.cur')) return; // click inside the picker
  closeCurMenus();
});
document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeCurMenus(); });

// Mobile Menu sheet: the bottom tab bar's Menu tab toggles header.nav-open (the slide-up nav
// sheet, #site-nav). Close it on link tap, outside click, Escape, or resize back to desktop.
// NB: scope "inside" checks to #site-nav — the tab bar is ALSO a <nav>, so a bare `nav` closest()
// would treat tab taps as inside the sheet and never close it.
function closeNav() {
  const h = document.querySelector('header.nav-open');
  if (!h) return;
  h.classList.remove('nav-open');
  const b = document.querySelector('.mtab[data-tab="menu"]');
  if (b) b.setAttribute('aria-expanded', 'false');
}
document.addEventListener('click', function (e) {
  if (!document.querySelector('header.nav-open')) return;
  if (e.target.closest('.mtab[data-tab="menu"]')) return;                      // the Menu toggle itself
  if (e.target.closest('#site-nav a') || !e.target.closest('#site-nav')) closeNav(); // link tap or outside the sheet
});
document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeNav(); });
window.addEventListener('resize', function () { if (window.innerWidth > 860) closeNav(); });

// Bottom tab bar: mark the active tab (re-runs per Turbo nav since the bar re-renders with <body>).
function markTab() {
  const p = location.pathname;
  const tab = document.body.classList.contains('is-listening-booth') ? 'shop'
    : /\/(products|collections)/.test(p) ? 'shop' : /\/search/.test(p) ? 'search' : /\/cart/.test(p) ? 'cart' : /\/account/.test(p) ? 'menu' : '';
  document.querySelectorAll('.mtab').forEach(t => t.classList.toggle('on', !!tab && t.dataset.tab === tab));
}

// Lite-embed facade: swap the poster for the autoplaying iframe on click. Attached once
// (theme.js is evaluated a single time in the <head>), so Turbo navigations don't stack it.
document.addEventListener('click', function (e) {
  const f = e.target.closest && e.target.closest('.yt-facade');
  if (!f) return;
  const w = f.parentNode, src = w.getAttribute('data-embed');
  if (!src) return;
  const ifr = document.createElement('iframe');
  ifr.className = 'ytframe'; ifr.src = src; ifr.title = w.getAttribute('data-title') || 'Preview';
  ifr.allow = 'autoplay; encrypted-media; picture-in-picture'; ifr.referrerPolicy = 'strict-origin-when-cross-origin'; ifr.allowFullscreen = true;
  w.innerHTML = ''; w.appendChild(ifr);
});

// Anonymous per-track YouTube (data-yt rows): swap the ONE shared preview panel (.ytpreview)
// to the track's video — no wall of embeds. Members use data-play rows → the bar player above.
document.addEventListener('click', function (e) {
  const r = e.target.closest && e.target.closest('.trk[data-yt]');   // whole row is the tap target
  if (!r) return;
  const yt = r.getAttribute('data-yt'); if (!yt) return;
  const panel = document.querySelector('.ytpreview'); if (!panel) return;
  const ifr = document.createElement('iframe');
  ifr.className = 'ytframe';
  ifr.src = 'https://www.youtube-nocookie.com/embed/' + yt + '?autoplay=1&playsinline=1&rel=0&modestbranding=1&iv_load_policy=3&color=white';
  ifr.allow = 'autoplay; encrypted-media; picture-in-picture'; ifr.referrerPolicy = 'strict-origin-when-cross-origin'; ifr.allowFullscreen = true;
  panel.innerHTML = ''; panel.appendChild(ifr);
  document.querySelectorAll('.trk.vplaying').forEach(function (x) { x.classList.remove('vplaying'); });
  r.classList.add('vplaying');
  if (panel.scrollIntoView) panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
});

// Block pinch-zoom on iOS Safari (it ignores viewport user-scalable=no). Double-tap zoom is
// handled by touch-action:manipulation; the viewport meta handles input-focus zoom.
document.addEventListener('gesturestart', function (e) { e.preventDefault(); }, { passive: false });
document.addEventListener('gesturechange', function (e) { e.preventDefault(); }, { passive: false });

// Keyboard activation for the role="button" track rows (Enter/Space → click).
document.addEventListener('keydown', function (e) {
  if ((e.key === 'Enter' || e.key === ' ') && e.target.classList && e.target.classList.contains('trk')) {
    e.preventDefault(); e.target.click();
  }
});

// ---- wishlist (localStorage, reuses the drawer in wish mode) ----
deadwax.wishlist = (function () {
  const KEY = 'deadwax_wish';
  const $ = id => document.getElementById(id);
  let items = load();
  function load() { try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { return []; } }
  function persist() { localStorage.setItem(KEY, JSON.stringify(items)); }
  function has(id) { id = +id; return items.some(w => w.id === id); }
  function fromEl(el) {
    return { id: +el.dataset.wish, handle: el.dataset.handle, title: el.dataset.title, artist: el.dataset.artist, art: el.dataset.art, price: el.dataset.price, url: el.dataset.url, variant: +el.dataset.variant, sold: el.dataset.sold === '1', last: el.dataset.last === '1' };
  }
  function toggle(id, el) {
    id = +id;
    if (has(id)) { items = items.filter(w => w.id !== id); }
    else if (el && el.dataset.wish) { items.push(fromEl(el)); }
    persist(); syncBadge(); markHearts();
    const d = $('drawer'); if (d && d.classList.contains('up') && d.dataset.mode === 'wish') render();
  }
  function syncBadge() { document.querySelectorAll('.js-wish-count').forEach(b => { b.textContent = items.length; b.style.display = items.length ? 'flex' : 'none'; }); }
  function markHearts() {
    document.querySelectorAll('[data-wish]').forEach(el => {
      const on = has(el.dataset.wish);
      const label = on ? 'Remove from wishlist' : 'Save to wishlist';
      el.classList.toggle('on', on);
      el.setAttribute('aria-pressed', on ? 'true' : 'false');
      el.setAttribute('aria-label', label);
      if (el.hasAttribute('title')) el.setAttribute('title', label);
    });
  }
  function row(w) {
    const img = w.art ? '<img src="' + w.art + '" alt="">' : '';
    const add = w.sold ? '<span class="add" style="color:var(--faint);cursor:default">Sold out</span>' : '<span class="add" data-wmove="' + w.id + '">+ Add to cart</span>';
    const warn = (w.last && !w.sold) ? '<div class="wishlast">Last one — don\'t miss out</div>' : '';
    return '<div class="ditem" data-wid="' + w.id + '"><a class="th" href="' + w.url + '">' + img + '</a>' +
      '<div class="di"><div class="t">' + w.title + '</div><div class="a">' + w.artist + '</div>' + warn + add + '</div>' +
      '<div class="right"><div class="pr">' + w.price + '</div><span class="rm" data-wremove="' + w.id + '" title="Remove">✕</span></div></div>';
  }
  function render() {
    const head = $('drawerHead'), body = $('drawerBody');
    head.innerHTML = '<h3>Wishlist</h3><span class="x" data-close>✕</span>';
    if (!items.length) { body.innerHTML = '<div class="dempty"><svg viewBox="0 0 100 100"><use href="#skull"/></svg><div>No saved records yet.<br>Tap the heart on any record to save it.</div>' + ((deadwax.config && deadwax.config.socialFollow) ? '<div class="dempty-soc">' + deadwax.config.socialFollow + '</div>' : '') + '</div>'; return; }
    const buyable = items.filter(w => !w.sold).length;
    body.innerHTML = (buyable ? '<div class="wishtop"><button class="btn btn-primary" data-waddall>Add all to cart</button></div>' : '') + '<div class="ditems">' + items.map(row).join('') + '</div>';
  }
  function open() { const d = $('drawer'); d.dataset.mode = 'wish'; render(); d.classList.add('up'); $('scrim').classList.add('up'); }
  async function addAll() {
    const buyable = items.filter(w => !w.sold);
    for (const w of buyable) {
      await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: w.variant, quantity: 1, properties: { _artist: w.artist, ...(w.last ? { _max: '1' } : {}) } }) });
    }
    items = items.filter(w => w.sold); persist(); syncBadge(); markHearts();
    if (deadwax.cart.open) deadwax.cart.open();
  }
  function init() {
    syncBadge(); markHearts();
    document.addEventListener('click', function (e) {
      const t = e.target.closest('[data-wish]'); if (t) { e.preventDefault(); e.stopPropagation(); toggle(t.dataset.wish, t); return; }
      const mv = e.target.closest('[data-wmove]'); if (mv) { const w = items.find(x => x.id === +mv.dataset.wmove); if (w) { deadwax.cart.add(w.variant, 1, { artist: w.artist, max: w.last ? 1 : undefined }); items = items.filter(x => x.id !== w.id); persist(); syncBadge(); markHearts(); } return; }
      const rm = e.target.closest('[data-wremove]'); if (rm) { toggle(rm.dataset.wremove); return; }
      if (e.target.closest('[data-waddall]')) { addAll(); return; }
    });
  }
  return { toggle, has, open, render, init, list: () => items.slice() };
})();

// ---- The Hunt (find-it-for-me + sold-out register interest) ----
deadwax.hunt = (function () {
  const $ = id => document.getElementById(id);
  function open(type, meta) {
    const box = $('modalBox');
    if (type === 'help' && meta) {
      box.innerHTML =
        '<span class="x" data-modal-close>✕</span>' +
        '<h3>Help me get this</h3>' +
        '<div class="sub">This copy’s gone, but tell us you want it. Enough interest and we’ll hunt another down.</div>' +
        '<div class="rec"><div class="th">' + (meta.art ? '<img src="' + meta.art + '" alt="">' : '') + '</div><div><div class="t">' + (meta.title || '') + '</div><div class="a">' + (meta.artist || '') + (meta.catno ? ' · ' + meta.catno : '') + '</div></div></div>' +
        '<form><input type="hidden" name="record" value="' + ((meta.title || '') + ' — ' + (meta.artist || '')) + '"><input type="email" name="email" placeholder="you@email.com" required>' +
        '<button class="btn btn-primary" type="submit">Register interest</button></form>' +
        '<div class="fine">We’ll email you if we track one down, and add you to the deadwax list. Unsubscribe anytime.</div>';
    } else {
      box.innerHTML =
        '<span class="x" data-modal-close>✕</span>' +
        '<h3>Find it for me</h3>' +
        '<div class="sub">After something we don’t have in? Tell us what you’re chasing and we’ll try to track it down.</div>' +
        '<form><input type="text" name="looking_for" placeholder="Artist / title / cat no" required>' +
        '<textarea name="details" placeholder="Any details: pressing, colour, condition…"></textarea>' +
        '<input type="email" name="email" placeholder="you@email.com" required>' +
        '<button class="btn btn-primary" type="submit">Send request</button></form>' +
        '<div class="fine">Submitting adds you to the deadwax mailing list so we can reach you. Unsubscribe anytime.</div>';
    }
    $('modalScrim').classList.add('up');
  }
  function close() { $('modalScrim').classList.remove('up'); }
  function done(form) { form.parentElement.innerHTML = '<div class="ok2">✓ Got it. We’ll be in touch.</div>'; setTimeout(close, 1500); }
  async function submit(form) {
    const endpoint = (deadwax.config && deadwax.config.huntEndpoint) || '';
    const data = Object.fromEntries(new FormData(form).entries());
    if (endpoint) { try { await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }); } catch (e) {} }
    done(form);
  }
  function init() {
    document.addEventListener('click', function (e) {
      const h = e.target.closest('[data-hunt]');
      if (h) { e.preventDefault(); e.stopPropagation(); open('help', { handle: h.dataset.hunt, title: h.dataset.title, artist: h.dataset.artist, art: h.dataset.art, catno: h.dataset.catno }); return; }
      if (e.target.closest('[data-modal-close]') || e.target.id === 'modalScrim') { close(); return; }
    });
    document.addEventListener('submit', function (e) {
      const f = e.target.closest('#modalBox form');
      if (f) { e.preventDefault(); submit(f); }
    });
  }
  return { open, close, init };
})();

// ---- predictive search: live autocomplete panel (rendered by the predictive-search section) ----
deadwax.search = (function () {
  const $ = id => document.getElementById(id);
  let t = null, lastQ = '';
  function open() {
    const p = $('searchPanel'), s = $('searchScrim'); if (!p) return;
    s.classList.add('up'); p.classList.add('up');
    const i = $('searchInput'); if (i) { i.focus(); i.select(); }
  }
  function close() { const p = $('searchPanel'), s = $('searchScrim'); if (p) p.classList.remove('up'); if (s) s.classList.remove('up'); }
  function run(q) {
    const box = $('searchResults'); if (!box) return;
    box.classList.add('loading');
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    fetch(root + 'search?q=' + encodeURIComponent(q) + '&section_id=predictive-search&type=product&options%5Bprefix%5D=last')
      .then(r => r.text())
      .then(html => { box.classList.remove('loading'); box.innerHTML = html.trim() || '<div class="ps-empty">Search is unavailable right now.</div>'; })
      .catch(() => { box.classList.remove('loading'); box.innerHTML = '<div class="ps-empty">Search failed — try again.</div>'; });
  }
  function init() {
    const i = $('searchInput'); if (!i) return;
    i.addEventListener('input', function () {
      const q = i.value.trim();
      clearTimeout(t);
      if (q.length < 2) { const b = $('searchResults'); if (b) b.innerHTML = ''; lastQ = ''; return; }
      t = setTimeout(function () { if (q !== lastQ) { lastQ = q; run(q); } }, 220);
    });
    const x = $('searchClose'); if (x) x.addEventListener('click', close);
    const s = $('searchScrim'); if (s) s.addEventListener('click', close);
    const res = $('searchResults'); if (res) res.addEventListener('click', function (e) { if (e.target.closest('.ps-item') || e.target.closest('.ps-all')) close(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
  }
  return { open, close, init };
})();

// Related records on the product page. The genre row is server-rendered (crawlable,
// no-JS friendly). This upgrades it to "More from <artist>" when the artist has other
// in-stock records — otherwise the genre row is left as-is. One fetch per PDP.
deadwax.moreRecs = (function () {
  function refresh() {
    const box = document.getElementById('moreRecs');
    if (!box || box.dataset.enhanced) return;
    const artist = box.dataset.artist, current = box.dataset.current;
    if (!artist) return;
    box.dataset.enhanced = '1';
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    fetch(root + 'search?q=' + encodeURIComponent(artist) + '&type=product&options%5Bunavailable_products%5D=last&section_id=artist-more')
      .then(r => r.text())
      .then(html => {
        const tmp = document.createElement('div');
        tmp.innerHTML = html.trim();
        const grid = tmp.querySelector('.grid');
        if (!grid) return;
        // Drop the current product's own card (matched by its /products/<handle> link).
        grid.querySelectorAll('.card').forEach(function (c) {
          const link = c.querySelector('a.sleeve'), href = link && link.getAttribute('href');
          if (href && href.split('?')[0].replace(/\/+$/, '').endsWith('/products/' + current)) c.remove();
        });
        if (!grid.querySelector('.card')) return;             // no artist siblings — keep the genre row
        box.querySelector('[data-more-grid]').replaceWith(grid);
        const title = box.querySelector('[data-more-title]');
        if (title) title.textContent = 'More from ' + artist;
        if (deadwax.player && deadwax.player.refresh) deadwax.player.refresh();  // highlight now-playing on injected cards
      })
      .catch(function () {});
  }
  return { refresh };
})();

// Bootstrap. Turbo Drive keeps the JS runtime and delegated document listeners alive
// across navigations, so module init must run exactly once (re-running would rebuild
// the player's Audio object and re-bind listeners). turbo:load fires on the first load
// and every subsequent visit; the guard keeps init one-time while each visit re-applies
// the "now playing" highlight to the freshly-rendered cards.
let booted = false;
function boot() {
  if (booted) return;
  booted = true;
  if (deadwax.theme.init) deadwax.theme.init();
  if (deadwax.cart.init) deadwax.cart.init();
  if (deadwax.player.init) deadwax.player.init();
  if (deadwax.wishlist.init) deadwax.wishlist.init();
  if (deadwax.hunt.init) deadwax.hunt.init();
  if (deadwax.search.init) deadwax.search.init();
  if (deadwax.journey && deadwax.journey.syncServer) deadwax.journey.syncServer(); // pull server views once (logged-in)
}
document.addEventListener('DOMContentLoaded', function () {  // fallback if Turbo is unavailable
  boot(); markTab();
  if (deadwax.theme.refresh) deadwax.theme.refresh();
  if (deadwax.journey) deadwax.journey.track();
  if (deadwax.moreRecs.refresh) deadwax.moreRecs.refresh();
});
document.addEventListener('turbo:load', function () {
  boot(); markTab();          // tab bar re-renders with <body> each visit — re-mark the active tab
  if (deadwax.theme.refresh) deadwax.theme.refresh();
  if (deadwax.journey) deadwax.journey.track();   // record this product view for the cart upsell
  if (deadwax.player.refresh) deadwax.player.refresh();
  if (deadwax.moreRecs.refresh) deadwax.moreRecs.refresh();   // per-PDP: fetch "More from artist"
});
